<script setup lang="ts">
import type {Populated} from '~/types/populated'
import type {Project, Industry, Media} from '~/types/legacy'

type ProjectPreview = Omit<Populated<Project>, 'industries'> & {
  industries: string[]
  industriesSlugs: string[]
  countryName?: string
  image: Populated<Media>
}

const props = withDefaults(
  defineProps<{
    project: ProjectPreview
    industry?: Populated<Industry>
    isMirrored?: boolean
  }>(),
  {
    isMirrored: false,
  }
)

const localePath = useLocalePath()

const slug = computed(() =>
  props.industry?.slug
    ? `/projects/${props.industry.slug}/${props.project.slug}`
    : `/projects/${props.project.industriesSlugs[0]}/${props.project.slug}`
)
</script>

<template>
  <article class="project flex flex-col-reverse px-2" :class="isMirrored ? 'md:flex-row-reverse' : 'md:flex-row'">
    <div
      class="project-content mt-8 flex w-full flex-col justify-end gap-y-4 md:mt-0 md:w-1/2 lg:w-1/3"
      :class="{
        'px-8 md:pr-16 md:text-left': isMirrored,
        'px-8 md:pl-16 md:text-right': !isMirrored,
      }"
    >
      <div
        class="flex justify-start"
        :class="{
          'md:justify-start': isMirrored,
          'md:justify-end': !isMirrored,
        }"
      >
        <p class="text-gray-500">
          {{ project.industries.join(', ') }}

          <span v-if="project.countryName"> — </span>

          <span v-if="project.countryName">{{ project.countryName }}</span>
        </p>
      </div>

      <Heading size="medium" class="text-main-950">
        {{ props.project.title }}
      </Heading>

      <Paragraph size="normal" class="text-gray-700">
        {{ props.project.description }}
      </Paragraph>

      <nuxt-link
        :to="localePath(slug)"
        class="group flex items-center py-3 font-bold text-main-600"
        :class="{'md:justify-end': !isMirrored}"
      >
        {{ $t('common.see_how_it_works') }}
        <Icon name="lucide:move-right" size="18" class="ml-2 transition group-hover:translate-x-1" />
      </nuxt-link>
    </div>

    <div class="project-image w-full md:w-1/2 lg:w-2/3">
      <div class="aspect-video rounded-lg bg-white p-2 bordershadow-medium">
        <NuxtImg
          v-if="project.image"
          v-bind="resolveMediaProps(project.image)"
          :width="600"
          :height="400"
          class="h-full w-full rounded object-cover bordershadow-gray"
          fit="cover"
        />
      </div>
    </div>
  </article>
</template>

<style lang="postcss" scoped></style>
